import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useAuth, signOut } from '@/legacy/redux';
import find from 'lodash/find';
import { getActiveTenancy, TenancyDialog } from '@/features/tenancy';
import { useRouting } from '@/hooks/use-routing';
import { useHasRole, useIsAdmin } from "@/auth/permissions";
import { useTranslation } from 'react-i18next';
import { PreferencesModal, type PreferencesTab } from '@/features/preferences';
import { LangSelect } from './LangSelect';
import { FORCE_TENANCY } from '@/config/constants';
import { useErrorReporting } from '@/ErrorReporting';
import DropdownMenu from "@imminently/decisively-ui/DropdownMenu";
import Icon from "@imminently/decisively-ui/Icon";
import Avatar from "@imminently/decisively-ui/Avatar";
import { getUserDisplayName, getUserInitials } from '@/lib/user';
import Tooltip from '@imminently/decisively-ui/Tooltip';


export const UserMenu: React.FC = () => {
  const { user } = useAuth();
  const { goToPage } = useRouting();
  const isAdmin = useIsAdmin();
  const canAccessEvents = useHasRole(["timesheet-admin", "superadmin", "owner"]);
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [preferencesOpen, setPreferencesOpen] = useState(false);
  const [preferencesInitialTab, setPreferencesInitialTab] = useState<PreferencesTab>("appearance");
  const [tenancyOpen, setTenancyOpen] = useState(false);

  const openPreferences = (tab: PreferencesTab = "appearance") => {
    setPreferencesInitialTab(tab);
    setPreferencesOpen(true);
  };
  const errorReporting = useErrorReporting();

  const tenantId = getActiveTenancy();
  const tenant = tenantId && user.groups ? find(user.groups, { id: tenantId }) : null;

  return (
    <>
      <DropdownMenu>
        <Tooltip placement="left" title={getUserDisplayName(user)}>
          <DropdownMenu.Trigger asChild>
            <Avatar className="cursor-pointer" invert alt={getUserDisplayName(user)} size="md">
              {getUserInitials(user)}
            </Avatar>
          </DropdownMenu.Trigger>
        </Tooltip>

        <DropdownMenu.Content align="end" className="w-50" asChild>
          <div className="p-1">
            <div className="p-2">
              <p className="text-base font-semibold leading-normal">
                {getUserDisplayName(user)}
              </p>
              <p className="text-xs text-muted-foreground">
                {user.email}
              </p>
            </div>

            <DropdownMenu.Separator />

            {/* Put tenancy at the top, as it helps the user quickly see who and where they are */}
            {FORCE_TENANCY ? (
              <DropdownMenu.Item disabled>
                <DropdownMenu.Item.Icon><Icon name="home" size={16} /></DropdownMenu.Item.Icon>
                <DropdownMenu.Item.Label>{FORCE_TENANCY}</DropdownMenu.Item.Label>
              </DropdownMenu.Item>
            ) : (
              tenant ? (
                <DropdownMenu.Item onClick={() => setTenancyOpen(true)}>
                  <DropdownMenu.Item.Icon><Icon name="home" size={16} /></DropdownMenu.Item.Icon>
                  <DropdownMenu.Item.Label>{tenant.name}</DropdownMenu.Item.Label>
                </DropdownMenu.Item>
              ) : null
            )}

            <DropdownMenu.Separator />

            <DropdownMenu.Item onClick={() => openPreferences("appearance")}>
              <DropdownMenu.Item.Icon><Icon name="settings-2" size={16} /></DropdownMenu.Item.Icon>
              <DropdownMenu.Item.Label>Preferences</DropdownMenu.Item.Label>
            </DropdownMenu.Item>

            <LangSelect />

            <DropdownMenu.Separator />

            <DropdownMenu.Item onClick={() => errorReporting.open()}>
              <DropdownMenu.Item.Icon><Icon name="message-square-share" size={16} /></DropdownMenu.Item.Icon>
              <DropdownMenu.Item.Label>{t('feedback')}</DropdownMenu.Item.Label>
              <DropdownMenu.Shortcut keys={['mod', 'shift', 'f']} />
            </DropdownMenu.Item>

            {canAccessEvents && (
              <DropdownMenu.Item onClick={() => goToPage('/events')}>
                <DropdownMenu.Item.Icon><Icon name="trigger" size={16} /></DropdownMenu.Item.Icon>
                <DropdownMenu.Item.Label>Tenancy Events</DropdownMenu.Item.Label>
              </DropdownMenu.Item>
            )}

            <DropdownMenu.Item onClick={() => window.open('https://imminently.betteruptime.com/', '_blank')?.focus()}>
              <DropdownMenu.Item.Icon><Icon name="square-activity" size={16} /></DropdownMenu.Item.Icon>
              <DropdownMenu.Item.Label>System Status</DropdownMenu.Item.Label>
              <DropdownMenu.Item.Icon><Icon name="open-in-new" size={16} /></DropdownMenu.Item.Icon>
            </DropdownMenu.Item>


            {isAdmin && (
              <>
                <DropdownMenu.Separator />
                <DropdownMenu.Item onClick={() => goToPage('/admin')}>
                  <DropdownMenu.Item.Icon><Icon name="settings" size={16} /></DropdownMenu.Item.Icon>
                  <DropdownMenu.Item.Label>{t('administration')}</DropdownMenu.Item.Label>
                </DropdownMenu.Item>
              </>
            )}

            <DropdownMenu.Separator />

            <DropdownMenu.Item onClick={() => dispatch(signOut())}>
              <DropdownMenu.Item.Icon><Icon name="log-out" size={16} /></DropdownMenu.Item.Icon>
              <DropdownMenu.Item.Label>{t('sign_out')}</DropdownMenu.Item.Label>
            </DropdownMenu.Item>
          </div>
        </DropdownMenu.Content>
      </DropdownMenu>

      <PreferencesModal open={preferencesOpen} onOpenChange={setPreferencesOpen} initialTab={preferencesInitialTab} />
      <TenancyDialog open={tenancyOpen} onOpenChange={setTenancyOpen} />
    </>
  );
};
